import { delay } from "redux-saga";
import { call, put, takeLatest, select } from "redux-saga/effects";
import { push } from "react-router-redux";

import * as configUtil from "util/config";
import * as config from "middleware/config";

import { MAX_FAILED_TIMES, TIME_IN_MILLIS_TO_REFRESH } from "constants.js";
import { globalTypes } from "reducers/types/global";
import { CONFIG_SOURCE_LEGACY, CONFIG_SOURCE_SPLIT, types, selectors, isSplitEndpointsEnabled } from "reducers/config";
import { types as i18nTypes, selectors as i18nSelector } from "reducers/i18n";
import { types as loginTypes } from "reducers/login";
import { types as sessionTypes, selectors as sessionSelectors } from "reducers/session";
import { initProvidersClickstreaming } from "util/clickstreaming";

/** Backend code returned by configuration.listConfiguration when the legacy endpoint is disabled. */
const LEGACY_DISABLED_CODE = "COR004E";

/** Once legacy answers COR004E, stop probing it on every refresh (ARBCMFIMP-19874). */
let skipLegacyConfiguration = false;

function isLegacyDisabledError(error) {
    return Boolean(error && error.data && error.data.code === LEGACY_DISABLED_CODE);
}

function* updateConfig() {
    while (true) {
        try {
            const timesFailed = yield select(selectors.getTimesFailed);

            if (timesFailed >= MAX_FAILED_TIMES) {
                yield put(push({ pathname: "/serverError" }));
            }

            yield put({ type: types.UPDATE_REQUEST });
            const data = yield call(loadConfiguration);
            yield call(setInitialLanguage, data);
            initProvidersClickstreaming();
            yield call(
                delay,
                configUtil.getTimeInMillis("frontend.configuration.refresh.interval", TIME_IN_MILLIS_TO_REFRESH),
            );
        } catch (err) {
            yield put({ type: types.UPDATE_FAILURE });
            yield call(delay, TIME_IN_MILLIS_TO_REFRESH);
        }
    }
}

/**
 * Loads frontend configuration.
 *
 * ARBCMFIMP-19874: always probe preLogin first, so a browser without persisted config does
 * not depend on the legacy endpoint to discover the split switch. Legacy is only used when
 * the split probe itself fails.
 */
export function* loadConfiguration() {
    try {
        return yield call(loadSplitConfiguration);
    } catch (splitError) {
        // eslint-disable-next-line no-console
        console.warn("Split configuration unavailable, falling back to legacy", splitError);
        return yield call(loadLegacyConfiguration);
    }
}

export function* loadLegacyConfiguration() {
    const legacyResponse = yield call(config.listConfiguration);
    if (legacyResponse.status !== 304) {
        const legacyItems = legacyResponse.data.data;
        yield put({ type: types.UPDATE_SUCCESS, items: legacyItems, source: CONFIG_SOURCE_LEGACY });
        return legacyItems;
    }
    return yield select(selectors.getConfig);
}

export function* finalizeSplitConfiguration(preLoginItems) {
    const isLoggedIn = yield select(sessionSelectors.isLoggedIn);
    if (!isLoggedIn) {
        yield put({ type: types.UPDATE_SUCCESS, items: preLoginItems, source: CONFIG_SOURCE_SPLIT });
        return preLoginItems;
    }

    const postLoginResponse = yield call(config.listPostLoginConfiguration);
    const currentItems = yield select(selectors.getConfig);
    const postLoginItems = postLoginResponse.status === 304 ? currentItems : postLoginResponse.data.data;
    const mergedItems = { ...postLoginItems, ...preLoginItems };
    yield put({ type: types.UPDATE_SUCCESS, items: mergedItems, source: CONFIG_SOURCE_SPLIT });
    return mergedItems;
}

export function* loadLegacyOrKeepSplit(preLoginItems) {
    if (skipLegacyConfiguration) {
        return yield call(finalizeSplitConfiguration, preLoginItems);
    }

    try {
        return yield call(loadLegacyConfiguration);
    } catch (legacyError) {
        if (isLegacyDisabledError(legacyError)) {
            skipLegacyConfiguration = true;
        } else {
            // eslint-disable-next-line no-console
            console.warn("Legacy configuration unavailable, keeping split configuration", legacyError);
        }
        return yield call(finalizeSplitConfiguration, preLoginItems);
    }
}

export function* loadSplitConfiguration() {
    const preLoginResponse = yield call(config.listPreLoginConfiguration);
    let preLoginItems = yield select(selectors.getPreLoginConfig);

    if (preLoginResponse.status !== 304) {
        preLoginItems = preLoginResponse.data.data;
        yield put({ type: types.PRE_LOGIN_UPDATE_SUCCESS, items: preLoginItems });
    }

    if (!isSplitEndpointsEnabled(preLoginItems)) {
        return yield call(loadLegacyOrKeepSplit, preLoginItems);
    }

    skipLegacyConfiguration = false;
    return yield call(finalizeSplitConfiguration, preLoginItems);
}

function* setInitialLanguage(data) {
    const initialLang = yield select(i18nSelector.getLang);
    if (!initialLang) {
        const defaultLang = data["frontend.i18n.default.lang"] || "es";
        yield put({
            type: i18nTypes.SET_INIT_LANG,
            lang: defaultLang,
        });
        configUtil.setRecaptchaLang(defaultLang);
        return;
    }
    configUtil.setRecaptchaLang(initialLang);
}

const sagas = [
    takeLatest(
        [globalTypes.INIT, types.RESET_SAGAS_UPDATE, loginTypes.LOGIN_SUCCESS, sessionTypes.LOGOUT_REQUEST],
        updateConfig,
    ),
];

export default sagas;
